// [生成时间:2026-06-23], [生成当前代码的ai模型名称:]

import Cookies from 'js-cookie'

/**
 * 微信授权配置
 * 每个配置对应一套用户体系，通过命名空间隔离 token
 */
const WX_AUTH_CONFIGS = {
  // 减脂营授权配置
  wegymer: {
    // 授权域名
    authDomain: 'btwx.gymcity.com.cn',
    // 授权接口路径
    authPath: '/wxAuth',
    // 授权类型
    authType: 'snsapi_userinfo',
    // 授权应用标识
    appId: 'wegymer',
    // 本地存储命名空间（用于 token 隔离）
    namespace: 'wegymer',
    // 路由前缀
    routePrefix: 'join-camp-question-v2',
    // Cookie key
    cookieKey: 'join-camp-token'
  },
  // 餐卡点评系统授权配置
  mealCardReview: {
    // 授权域名
    authDomain: 'btwx.gymcity.com.cn',
    // 授权接口路径
    authPath: '/wxAuth',
    // 授权类型
    authType: 'snsapi_userinfo',
    // 授权应用标识
    appId: 'wegymer',
    // 本地存储命名空间（用于 token 隔离）
    namespace: 'meal_card_review',
    // 路由前缀
    routePrefix: 'meal-card-review-system',
    // Cookie key
    cookieKey: 'meal-card-review-token'
  }
  // 可以在这里新增其他授权配置，例如：
  // otherSystem: {
  //   authDomain: 'other-auth.example.com',
  //   authPath: '/oauth/wechat',
  //   authType: 'snsapi_base',
  //   appId: 'other-app',
  //   namespace: 'other',
  //   routePrefix: 'other-system',
  //   cookieKey: 'other-token'
  // }
}

/**
 * 微信授权管理类
 * 支持多用户体系，通过命名空间隔离不同系统的 token
 */
class WxAuthManager {
  constructor() {
    this.currentConfigKey = null
  }

  /**
   * 获取授权配置
   * @param {string} configKey 配置键名，默认为 'wegymer'
   * @returns {Object} 授权配置
   */
  getConfig(configKey = 'wegymer') {
    return WX_AUTH_CONFIGS[configKey]
  }

  /**
   * 设置当前授权配置
   * @param {string} configKey 配置键名
   */
  setConfig(configKey) {
    this.currentConfigKey = configKey
    return this
  }

  /**
   * 生成本地存储 key（通过命名空间隔离）
   * @param {string} key 原始 key
   * @param {string} namespace 命名空间
   * @returns {string} 带命名空间的 key
   */
  getStorageKey(key, namespace) {
    return `${namespace}_${key}`
  }

  /**
   * 从 URL 或本地存储获取 signToken
   * @param {string} configKey 配置键名，默认为 'wegymer'
   * @returns {string} signToken
   */
  getSignToken(configKey = 'wegymer') {
    const config = this.getConfig(configKey)
    const { namespace } = config

    // 优先从 URL 参数获取
    const urlParams = new URLSearchParams(window.location.search)
    const urlToken = urlParams.get('sign_token')
    if (urlToken) {
      // 保存到本地存储
      const storageKey = this.getStorageKey('signToken', namespace)
      localStorage.setItem(storageKey, urlToken)
      return urlToken
    }

    // 从本地存储获取
    const storageKey = this.getStorageKey('signToken', namespace)
    return localStorage.getItem(storageKey) || ''
  }

  /**
   * 获取用户 token（从 Cookie）
   * @param {string} configKey 配置键名，默认为 'wegymer'
   * @returns {string} 用户 token
   */
  getUserToken(configKey = 'wegymer') {
    const config = this.getConfig(configKey)
    const { cookieKey } = config
    return Cookies.get(cookieKey) || ''
  }

  /**
   * 检查用户是否已授权
   * @param {string} configKey 配置键名，默认为 'wegymer'
   * @returns {boolean} 是否已授权
   */
  isAuthorized(configKey = 'wegymer') {
    const signToken = this.getSignToken(configKey)
    const userToken = this.getUserToken(configKey)
    return !!signToken && !!userToken
  }

  /**
   * 跳转到微信授权页面
   * @param {Object} options 配置选项
   * @param {string} options.configKey 配置键名，默认为 'wegymer'
   * @param {string} options.redirectUrl 授权后的回调地址，默认为当前页面
   * @param {string} options.scope 授权作用域，可选值: snsapi_base|snsapi_userinfo
   * @param {string} options.state 自定义状态参数
   */
  redirectToAuth(options = {}) {
    const {
      configKey = 'wegymer',
      redirectUrl = location.href,
      scope,
      state
    } = options

    const config = this.getConfig(configKey)
    const { authDomain, authPath, authType, appId } = config

    // 使用配置的 scope，如果传入的 options.scope 则覆盖配置
    const finalScope = scope || authType

    // 构建授权 URL
    const authUrl = `https://${authDomain}${authPath}?auth=${appId}&scope=${finalScope}&next=${encodeURIComponent(
      redirectUrl
    )}`

    // 添加 state 参数
    const finalUrl = state ? `${authUrl}&state=${state}` : authUrl

    // 跳转到授权页面
    window.location.href = finalUrl
  }

  /**
   * 授权检查和跳转（封装常用逻辑）
   * @param {Object} options 配置选项
   * @param {string} options.configKey 配置键名，默认为 'wegymer'
   * @param {Function} options.onAuthorized 已授权时的回调
   * @param {Function} options.onUnauthorized 未授权时的回调
   * @param {Function} options.onNoToken 没有 signToken 时的回调
   * @param {string} options.redirectUrl 授权后的回调地址，默认为当前页面
   * @returns {Object} 授权状态信息
   */
  checkAuth(options = {}) {
    const {
      configKey = 'wegymer',
      onAuthorized,
      onUnauthorized,
      onNoToken,
      redirectUrl = location.href
    } = options

    const signToken = this.getSignToken(configKey)
    const userToken = this.getUserToken(configKey)

    const result = {
      hasSignToken: !!signToken,
      hasUserToken: !!userToken,
      isAuthorized: !!signToken && !!userToken,
      signToken,
      userToken
    }

    // 如果没有 signToken，需要跳转授权
    if (!signToken) {
      if (onNoToken) {
        onNoToken(result)
      } else {
        this.redirectToAuth({ configKey, redirectUrl })
      }
      return result
    }

    // 如果没有用户 token，跳转到登录页
    if (!userToken) {
      if (onUnauthorized) {
        onUnauthorized(result)
      }
      return result
    }

    // 已授权，执行回调
    if (onAuthorized) {
      onAuthorized(result)
    }

    return result
  }

  /**
   * 清除当前配置的授权信息
   * @param {string} configKey 配置键名，默认为 'wegymer'
   */
  clearAuth(configKey = 'wegymer') {
    const config = this.getConfig(configKey)
    const { namespace, cookieKey } = config

    // 清除本地存储
    const storageKey = this.getStorageKey('signToken', namespace)
    localStorage.removeItem(storageKey)

    // 清除 Cookie
    Cookies.remove(cookieKey)
  }

  /**
   * 跳转到指定路由（封装路由跳转，支持不同系统的路由前缀）
   * @param {string} route 路由路径
   * @param {string} configKey 配置键名，默认为 'wegymer'
   */
  pushTo(route, configKey = 'wegymer') {
    const config = this.getConfig(configKey)
    const { routePrefix } = config

    // 如果配置了路由前缀，则自动添加
    const finalRoute =
      routePrefix && !route.startsWith('/') ? `/${routePrefix}/${route}` : route

    if (this.$router) {
      this.$router.push(finalRoute)
    } else if (window.Vue && window.Vue.prototype.$router) {
      window.Vue.prototype.$router.push(finalRoute)
    } else {
      console.warn('[WxAuthManager] 未找到路由实例')
    }
  }

  /**
   * 设置 Vue 实例（用于访问 $router 和 $route）
   * @param {Object} vue Vue 实例
   */
  setVueInstance(vue) {
    this.$router = vue.$router
    this.$route = vue.$route
    this.$getQuery = vue.$http?._getQuery
  }

  /**
   * 新增授权配置
   * @param {string} key 配置键名
   * @param {Object} config 配置对象
   */
  addConfig(key, config) {
    if (!key || !config) {
      throw new Error('[WxAuthManager] 配置键名和配置对象不能为空')
    }

    // 验证必填字段
    const requiredFields = [
      'authDomain',
      'authPath',
      'authType',
      'appId',
      'namespace',
      'cookieKey'
    ]

    const missingFields = requiredFields.filter((field) => !config[field])
    if (missingFields.length > 0) {
      throw new Error(
        `[WxAuthManager] 缺少必填字段: ${missingFields.join(', ')}`
      )
    }

    WX_AUTH_CONFIGS[key] = config
  }
}

// 创建单例
const wxAuthManager = new WxAuthManager()

// 自动绑定 Vue 实例
if (typeof window !== 'undefined' && window.Vue) {
  window.Vue.mixin({
    created() {
      if (!wxAuthManager.$router) {
        wxAuthManager.setVueInstance(this)
      }
    }
  })
}

export default wxAuthManager
